import React from 'react'
import _cx from 'classnames/bind'
import PropTypes from 'prop-types'
import { Icon, Text, Modal } from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import * as CostCalculations from 'common/modules/bidBanner/costCalculations'
import analytics from '../../../services/analytics'
import ModalAddress from '../modalAddress'
import cls from './styles.scss'
const cx = _cx.bind(cls)

const CURRENT_BID_INCREASE_WARNING =
  'The current bid has increased. The calculations have been updated.'
const EARNEST_MONEY_DEPOSIT_EXPLANATION =
  'Initial down payment due 2 business days after auction ends'

class PurchaseAgreementLink extends React.Component {
  static propTypes = {
    elmId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    purchaseAgreement: PropTypes.object,
  }

  trackClick = () => {
    analytics.documents(this.props.purchaseAgreement)
  }

  render() {
    const { elmId, label, purchaseAgreement } = this.props
    return (
      <div data-elm-id={elmId}>
        <Text color='gray' data-elm-id='label' size='small'>
          {label}
        </Text>
        <div className={cx('purchase-agreement-row', 'u-mb-2')}>
          <Text color='grayMedium' data-elm-id='explanation' size='tiny'>
            Seller or buyer may pay
          </Text>
          <Text data-elm-id='document_note' size='tiny'>
            {purchaseAgreement ? (
              <a
                data-elm-id='purchase_agreement_link'
                href={purchaseAgreement.media_download_uri}
                onClick={this.trackClick}
                target='blank'
              >
                See Purchase Agreement
              </a>
            ) : (
              'Not Available'
            )}
          </Text>
        </div>
      </div>
    )
  }
}

export default class CostBreakdown extends React.Component {
  static propTypes = {
    bidAmount: PropTypes.number,
    isBidAmountValid: PropTypes.bool.isRequired,
    segment: PropTypes.object.isRequired,
    listing: PropTypes.object.isRequired,
  }

  state = {
    bidAmountOnOpen: null,
    isModalOpen: false,
  }

  showModal = () => {
    this.setState({
      bidAmountOnOpen: this.props.bidAmount,
      isModalOpen: true,
    })
  }

  hideModal = () => {
    this.setState({
      bidAmountOnOpen: null,
      isModalOpen: false,
    })
  }

  get shouldShowWarning() {
    return (
      this.props.segment.__methods__.isOnlineAuctionActive() &&
      this.props.bidAmount > this.state.bidAmountOnOpen
    )
  }

  render() {
    const { bidAmount, isBidAmountValid, listing, ...rest } = this.props
    const calculations = listing.__methods__.getCostCalculations(bidAmount)
    const purchaseAgreement = listing.applicable_global_documents
      ? listing.applicable_global_documents?.find(
          doc =>
            doc.media_purpose_type_code ===
            graph.enums.GlobalMediaPurpose.PurchaseAgreement
        )
      : undefined // Or handle the case where applicable_global_document is missing
    const isCostBreakdownAvailable =
      listing.listing_configuration?.is_buyers_premium_available ||
      listing.listing_configuration?.is_earnest_money_deposit_available

    const { buyersPremium, totalPurchasePrice, earnestMoneyDeposit } =
      calculations

    const buyersPremiumExplanation =
      listing.__methods__.getBuyersPremiumExplanation(bidAmount)

    return isCostBreakdownAvailable ? (
      <>
        <div
          className={cx('u-pointer-cursor')}
          data-elm-id='cost_breakdown_target'
          onClick={this.showModal}
        >
          <Icon color='info' name='usd-circle' iconStyle='regular' fitted />
          <Text
            as='span'
            className='u-ml-1'
            color='info'
            content='Cost Breakdown'
            size='tiny'
          />
        </div>
        <Modal
          elmId='cost_breakdown_modal'
          mobileTitleSkin='light'
          onClose={this.hideModal}
          open={this.state.isModalOpen}
          size='tiny'
          title='Cost Breakdown'
          {...rest}
        >
          <Modal.Content>
            <ModalAddress addressTuple={listing.formatted_address} />
            {this.shouldShowWarning && (
              <div className={cx('caution-container', 'u-mb-3')}>
                <Icon
                  color='caution'
                  name='exclamation-circle'
                  size='large'
                  iconStyle='solid'
                  fitted
                />
                <Text color='grayDark' size='small' className='u-ml-2'>
                  {CURRENT_BID_INCREASE_WARNING}
                </Text>
              </div>
            )}
            <div className={cx('padding-right')}>
              {this.props.segment.__methods__.isOnlineAuctionActive() ? (
                <CostCalculations.YourBid value={bidAmount} />
              ) : (
                <CostCalculations.YourWinningBid value={bidAmount} />
              )}
              <CostCalculations.BuyersPremium
                hasError={!isBidAmountValid}
                value={buyersPremium}
                valueExplanation={buyersPremiumExplanation}
              />
            </div>
            <CostCalculations.TotalPurchasePrice
              hasError={!isBidAmountValid}
              labelWeight='heavy'
              value={totalPurchasePrice}
              valueBackground='grayExtraLight'
              valueColor='grayDark'
              valueWeight='heavy'
            />
            <div className={cx('divider', 'u-mt-3', 'padding-right')}>
              <Text
                color='grayDark'
                size='large'
                className='u-mb-2'
                data-elm-id='cost_breakdown_modal_subheader'
              >
                Keep these in mind
              </Text>
              <CostCalculations.EarnestMoneyDeposit
                hasError={!isBidAmountValid}
                value={earnestMoneyDeposit}
                valueExplanation={EARNEST_MONEY_DEPOSIT_EXPLANATION}
              />
              <PurchaseAgreementLink
                elmId='title_insurance'
                label='Title Insurance'
                purchaseAgreement={purchaseAgreement}
              />
              <PurchaseAgreementLink
                elmId='closing_costs'
                label='Closing Costs'
                purchaseAgreement={purchaseAgreement}
              />
            </div>
          </Modal.Content>
        </Modal>
      </>
    ) : null
  }
}
